import React, { useMemo } from "react";
import { SessionInfoTabs } from "./session_info_tabs";
import {
  type TestSuiteRunRecordingData,
  type CompanyData,
} from "../../utils/types";
import { Alert, AlertTitle, Button } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { styled as materialStyled } from "@mui/material/styles";

const RecordingInformation = ({
  runRecordingData,
  companyData,
}: {
  runRecordingData: TestSuiteRunRecordingData;
  companyData?: CompanyData;
}) => {
  const parentRun = runRecordingData.runs[runRecordingData.runs.length - 1];
  const location = useLocation();

  const hasFailureReport =
    parentRun.runData?.failureReport?.status === "completed";

  // Link to the failure analysis: the current recording URL with the `live`
  // param removed, which renders the generated failure report instead of the
  // live recording.
  const failureAnalysisHref = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete("live");
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  }, [location.pathname, location.search]);

  const titleSeverity = useMemo(() => {
    const status = parentRun.runData!.status;
    if (status === "failed") {
      return "error";
    } else if (status === "blocked" || status === "canceled") {
      return "warning";
    }
    return "success";
  }, [parentRun]);

  const displayMessage = useMemo(() => {
    const classification = parentRun.runData?.finalFailureClassification;
    if (
      classification === "agentic_failure_unresolved" ||
      classification === "false_pass_unresolved"
    ) {
      return "We weren't able to produce a valid test result even after iterating on your test prompt. Please review the insights per run and adjust your test.";
    }
    return parentRun.runData?.statusMessage;
  }, [parentRun]);

  return (
    <ContentWrapper>
      <CustomAlert
        severity={titleSeverity}
        action={
          hasFailureReport ? (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              component={RouterLink}
              to={failureAnalysisHref}
              sx={{ whiteSpace: "nowrap" }}
            >
              View Failure Analysis
            </Button>
          ) : undefined
        }
      >
        <AlertTitle sx={{ lineBreak: "anywhere" }}>
          {parentRun.runData!.test_title ?? parentRun.runData!.title}
        </AlertTitle>
        {displayMessage}
      </CustomAlert>
      <LogsSection>
        <SessionInfoTabs
          runRecordingData={runRecordingData}
          companyData={companyData}
        />
      </LogsSection>
    </ContentWrapper>
  );
};

const CustomAlert = materialStyled(Alert)(() => ({
  marginInline: "16px",
  marginTop: "8px",
}));

const ContentWrapper = styled.div`
  grid-area: content;
  display: grid;
  grid-template-areas:
    "title"
    "logs";
  column-gap: 12px;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  gap: 16px;
`;

const LogsSection = styled.div`
  grid-area: logs;
  display: grid;
  grid-template-areas:
    "tabs"
    "tab-content";
  grid-template-rows: auto minmax(0, 1fr);
`;

export default RecordingInformation;
